// Card name lists per suit, in the same order as workers/cards-pool.ts.
// Kept in sync manually — there are 24 entries per suit. Exported so
// renderers can iterate without a separate source of truth.
export const NAMES = {
  action: [
    "Signature", "Co-sign", "Witnessed mark", "Handshake", "Threshold cross",
    "Public declaration", "Submission", "Withdrawal", "Vote", "Bid",
    "Refusal", "Sustained presence", "Arrival", "Departure", "Gesture",
    "Speech", "Pairing", "Footprint", "Move", "Edit",
    "Reading", "Recording", "Forwarding", "Pause",
  ],
  observation: [
    "Self-report", "Single witness", "Quorum", "Majority", "Unanimity",
    "Authority", "No objection", "Photograph", "Logbook", "Receipt",
    "Counter", "Trace", "Stranger's account", "Crowd attention", "Long observation",
    "Sensor reading", "Threshold trip", "No record", "Re-examinable", "Aggregate only",
    "Anonymous", "Identified", "Delayed", "Immediate",
  ],
  relation: [
    "Queue", "Auction", "Lottery", "Round-robin", "Priority list",
    "By age", "By need", "By kinship", "By contribution", "Parent reference",
    "Chain", "Mesh", "Appointment", "Deadline", "Promise binding",
    "Debt binding", "Mutual binding", "Seat for the absent", "Seat for the future", "Seat for the past",
    "Treaty", "Fork", "Inheritance", "No order",
  ],
};

export function indexOf(suit, name) {
  const list = NAMES[suit];
  if (!list) return 0;
  const i = list.indexOf(name);
  return i < 0 ? 0 : i + 1;
}

// === SVG primitive helpers ====================================
// All coordinates are inside the 96×96 viewport. Each returns
// { d, kind }; consumers concatenate into a paths array.

function dot(cx, cy, r = 2.2) {
  return { d: `M ${cx - r} ${cy} a ${r} ${r} 0 1 0 ${2 * r} 0 a ${r} ${r} 0 1 0 ${-2 * r} 0 Z`, kind: "dot" };
}
function ring(cx, cy, r) {
  return { d: `M ${cx + r} ${cy} a ${r} ${r} 0 1 0 ${-2 * r} 0 a ${r} ${r} 0 1 0 ${2 * r} 0`, kind: "ring" };
}
function line(x1, y1, x2, y2) {
  return { d: `M ${x1} ${y1} L ${x2} ${y2}`, kind: "line" };
}
function dashedLine(x1, y1, x2, y2) {
  return { d: `M ${x1} ${y1} L ${x2} ${y2}`, kind: "dashed" };
}
function arc(cx, cy, r, a1, a2) {
  const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
  const x2 = cx + r * Math.cos(a2), y2 = cy + r * Math.sin(a2);
  const large = Math.abs(a2 - a1) > Math.PI ? 1 : 0;
  const sweep = a2 > a1 ? 1 : 0;
  return { d: `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} ${sweep} ${x2.toFixed(2)} ${y2.toFixed(2)}`, kind: "arc" };
}
function chevron(x, y, dx, dy, size = 6) {
  const ang = Math.atan2(dy, dx);
  const left = ang + Math.PI - 0.4;
  const right = ang + Math.PI + 0.4;
  return { d: `M ${(x + size * Math.cos(left)).toFixed(2)} ${(y + size * Math.sin(left)).toFixed(2)} L ${x} ${y} L ${(x + size * Math.cos(right)).toFixed(2)} ${(y + size * Math.sin(right)).toFixed(2)}`, kind: "chevron" };
}
function row(n, y, x0 = 16, x1 = 80) {
  const out = [];
  for (let i = 0; i < n; i++) {
    const x = x0 + (i / (n - 1)) * (x1 - x0);
    out.push(dot(x, y));
  }
  return out;
}
function ringRow(n, y, x0 = 16, x1 = 80, r = 4) {
  const out = [];
  for (let i = 0; i < n; i++) {
    const x = x0 + (i / (n - 1)) * (x1 - x0);
    out.push(ring(x, y, r));
  }
  return out;
}
function cluster(cx, cy, r = 6) {
  return [
    dot(cx, cy - r),
    dot(cx - r * 0.87, cy + r * 0.5),
    dot(cx + r * 0.87, cy + r * 0.5),
  ];
}

// === Per-card mark builders ===================================

const MARKS = {
  action: {
    "Signature":          () => [{ d: "M 18 70 C 30 30, 50 80, 78 38", kind: "line" }, dot(78, 38, 2.6)],
    "Co-sign":            () => [line(20, 72, 48, 30), line(76, 72, 48, 30), dot(48, 30, 2.6)],
    "Witnessed mark":     () => [line(22, 70, 60, 30), dot(60, 30, 2.6), dot(78, 56)],
    "Handshake":          () => [arc(36, 48, 18, -Math.PI/2, Math.PI/2), arc(60, 48, 18, Math.PI/2, 3*Math.PI/2)],
    "Threshold cross":    () => [line(16, 48, 80, 48), line(48, 22, 48, 74), chevron(80, 48, 1, 0)],
    "Public declaration": () => [arc(48, 60, 22, -Math.PI*0.9, -Math.PI*0.1), dot(48, 60, 2.6), dot(20, 30), dot(48, 22), dot(76, 30)],
    "Submission":         () => [line(48, 18, 48, 64), chevron(48, 64, 0, 1), ring(48, 76, 6)],
    "Withdrawal":         () => [line(48, 78, 48, 32), chevron(48, 32, 0, -1), ring(48, 20, 6)],
    "Vote":               () => [{ d: "M 28 28 L 68 28 L 68 68 L 28 68 Z", kind: "line" }, { d: "M 36 44 L 44 52 L 60 36", kind: "line" }],
    "Bid":                () => [line(20, 78, 76, 22), chevron(76, 22, 1, -1), dot(20, 78, 2.6)],
    "Refusal":            () => [ring(48, 48, 22), line(32, 32, 64, 64)],
    "Sustained presence": () => [line(20, 48, 76, 48), dot(20, 48, 2.6), dot(76, 48, 2.6), dashedLine(20, 56, 76, 56)],
    "Arrival":            () => [line(16, 48, 76, 48), chevron(76, 48, 1, 0), ring(82, 48, 4)],
    "Departure":          () => [ring(14, 48, 4), line(20, 48, 80, 48), chevron(80, 48, 1, 0)],
    "Gesture":            () => [{ d: "M 24 60 Q 40 24, 56 48 T 80 32", kind: "line" }],
    "Speech":             () => [{ d: "M 20 36 Q 20 22, 34 22 L 64 22 Q 78 22, 78 36 L 78 50 Q 78 64, 64 64 L 40 64 L 30 76 L 32 64 Q 20 64, 20 50 Z", kind: "line" }],
    "Pairing":            () => [dot(34, 48, 3.2), dot(62, 48, 3.2), line(40, 48, 56, 48)],
    "Footprint":          () => [dot(28, 70), dot(40, 56), dot(54, 42), dot(70, 28)],
    "Move":               () => [{ d: "M 22 28 L 22 68 L 74 68 L 74 28 Z", kind: "line" }, dot(34, 56, 3), { d: "M 50 56 L 66 40", kind: "line" }, chevron(66, 40, 1, -1)],
    "Edit":               () => [{ d: "M 22 70 L 60 32 L 70 42 L 32 80", kind: "line" }, line(22, 70, 32, 80)],
    "Reading":            () => [arc(48, 48, 22, 0.2, Math.PI - 0.2), dot(48, 48, 2.6)],
    "Recording":          () => [ring(48, 48, 18), dot(48, 48, 3.2), ring(48, 48, 6)],
    "Forwarding":         () => [line(18, 48, 60, 48), chevron(60, 48, 1, 0), line(48, 48, 80, 48), chevron(80, 48, 1, 0)],
    "Pause":              () => [{ d: "M 36 28 L 36 68", kind: "line" }, { d: "M 60 28 L 60 68", kind: "line" }],
  },
  observation: {
    "Self-report":       () => [dot(48, 48, 3.2), ring(48, 48, 10)],
    "Single witness":    () => [dot(38, 50, 3), dot(68, 38, 2.4), dashedLine(38, 50, 68, 38)],
    "Quorum":            () => [...ringRow(5, 48, 18, 78, 5), ring(30, 48, 8), ring(48, 48, 8), ring(66, 48, 8)],
    "Majority":          () => [dot(28, 48), dot(40, 48), dot(52, 48), dot(64, 48), dot(76, 48), ring(40, 48, 4), ring(52, 48, 4), ring(64, 48, 4)],
    "Unanimity":         () => [...ringRow(5, 48, 18, 78, 5)],
    "Authority":         () => [dot(48, 30, 4), line(36, 50, 60, 50), ...row(3, 70, 32, 64)],
    "No objection":      () => [...row(5, 48, 18, 78), dashedLine(18, 64, 78, 64)],
    "Photograph":        () => [{ d: "M 22 30 L 74 30 L 74 66 L 22 66 Z", kind: "line" }, ring(48, 48, 10), dot(64, 36, 1.6)],
    "Logbook":           () => [{ d: "M 22 22 L 74 22 L 74 74 L 22 74 Z", kind: "line" }, line(30, 36, 66, 36), line(30, 50, 66, 50), line(30, 64, 60, 64)],
    "Receipt":           () => [{ d: "M 30 18 L 66 18 L 66 70 L 60 76 L 54 70 L 48 76 L 42 70 L 36 76 L 30 70 Z", kind: "line" }, line(36, 36, 58, 36), line(36, 48, 58, 48)],
    "Counter":           () => [dot(20, 70), dot(28, 60), dot(36, 50), dot(44, 40), dot(52, 30), line(60, 22, 78, 22)],
    "Trace":             () => [{ d: "M 18 64 Q 36 36, 54 56 T 80 36", kind: "dashed" }, dot(80, 36, 2.4)],
    "Stranger's account":() => [dot(70, 32, 3), ring(70, 32, 8), dashedLine(28, 60, 60, 42), dot(28, 60)],
    "Crowd attention":   () => [...cluster(28, 36), ...cluster(64, 36), ...cluster(46, 70), dot(48, 48, 1.6)],
    "Long observation":  () => [line(18, 48, 78, 48), dot(18, 48), dot(34, 48), dot(50, 48), dot(66, 48), dot(78, 48, 3.2)],
    "Sensor reading":    () => [ring(48, 48, 18), { d: "M 48 30 L 48 48 L 64 56", kind: "line" }],
    "Threshold trip":    () => [{ d: "M 18 60 L 40 60 L 50 30 L 78 30", kind: "line" }, dot(50, 30, 2.4)],
    "No record":         () => [ring(48, 48, 22), { d: "M 32 32 L 64 64", kind: "dashed" }],
    "Re-examinable":     () => [arc(48, 48, 18, -Math.PI*0.9, Math.PI*0.7), chevron(30, 38, -1, 1), dot(48, 48, 2.4)],
    "Aggregate only":    () => [{ d: "M 18 70 L 30 50 L 42 60 L 54 35 L 66 45 L 78 25", kind: "dashed" }, dot(78, 25, 3)],
    "Anonymous":         () => [ring(48, 48, 14), { d: "M 30 64 Q 48 50, 66 64", kind: "dashed" }],
    "Identified":        () => [ring(48, 38, 10), { d: "M 30 70 Q 48 56, 66 70", kind: "line" }, dot(48, 38, 2.4)],
    "Delayed":           () => [dot(20, 48), dashedLine(26, 48, 70, 48), chevron(70, 48, 1, 0), dot(76, 48, 2.4)],
    "Immediate":         () => [dot(20, 48, 2.4), line(26, 48, 70, 48), chevron(70, 48, 1, 0), dot(76, 48, 2.4)],
  },
  relation: {
    "Queue":             () => [...row(5, 48), line(18, 64, 76, 64), chevron(76, 64, 1, 0)],
    "Auction":           () => [...row(5, 56), line(64, 56, 64, 30), chevron(64, 30, 0, -1), dot(64, 24, 3.2)],
    "Lottery":           () => [...row(5, 48), ring(48, 48, 8)],
    "Round-robin":       () => [...ringRow(5, 48, 22, 74, 5), arc(48, 48, 26, -Math.PI*0.95, -Math.PI*0.05)],
    "Priority list":     () => [dot(28, 26, 3.4), dot(28, 44), dot(28, 60), dot(28, 76, 1.6), line(36, 26, 78, 26), line(36, 44, 70, 44), line(36, 60, 60, 60)],
    "By age":            () => [dot(22, 48, 3.6), dot(38, 48, 3), dot(54, 48, 2.4), dot(70, 48, 1.8), dashedLine(22, 64, 76, 64)],
    "By need":           () => [dot(22, 60, 1.6), dot(38, 56, 2.2), dot(54, 50, 2.8), dot(70, 42, 3.4), line(18, 70, 78, 70)],
    "By kinship":        () => [...cluster(30, 36, 5), ...cluster(66, 60, 5), line(36, 40, 60, 56)],
    "By contribution":   () => [{ d: "M 18 72 L 30 60 L 42 50 L 54 42 L 66 36 L 78 28", kind: "line" }, dot(18, 72), dot(78, 28, 3.2)],
    "Parent reference":  () => [dot(48, 26, 3), dot(28, 56), dot(48, 56), dot(68, 56), line(48, 28, 28, 54), line(48, 28, 48, 54), line(48, 28, 68, 54)],
    "Chain":             () => [...row(5, 48), line(22, 48, 76, 48)],
    "Mesh":              () => [dot(24, 30), dot(72, 30), dot(48, 48), dot(24, 70), dot(72, 70), line(24, 30, 72, 30), line(24, 30, 24, 70), line(72, 30, 72, 70), line(24, 70, 72, 70), line(24, 30, 48, 48), line(72, 30, 48, 48), line(24, 70, 48, 48), line(72, 70, 48, 48)],
    "Appointment":       () => [...ringRow(5, 48, 18, 78, 4), dot(48, 48, 3.2)],
    "Deadline":          () => [line(20, 70, 70, 70), chevron(70, 70, 1, 0), line(70, 60, 70, 80), dot(34, 60), dot(50, 60)],
    "Promise binding":   () => [dot(28, 48), dot(68, 48), arc(48, 48, 22, -Math.PI*0.85, -Math.PI*0.15), dashedLine(32, 48, 64, 48)],
    "Debt binding":      () => [dot(28, 48), dot(68, 48), arc(48, 48, 22, Math.PI*0.15, Math.PI*0.85), dashedLine(32, 48, 64, 48)],
    "Mutual binding":    () => [dot(30, 48, 3), dot(66, 48, 3), arc(48, 48, 18, -Math.PI*0.9, -Math.PI*0.1), arc(48, 48, 18, Math.PI*0.1, Math.PI*0.9)],
    "Seat for the absent":  () => [...row(5, 48), ring(48, 48, 6)],
    "Seat for the future":  () => [dot(22, 48), dot(38, 48), dot(54, 48), ring(70, 48, 5), dashedLine(64, 48, 76, 48)],
    "Seat for the past":    () => [ring(26, 48, 5), dot(42, 48), dot(58, 48), dot(74, 48), dashedLine(20, 48, 32, 48)],
    "Treaty":            () => [dot(28, 30, 3), dot(68, 30, 3), dot(48, 70, 3), line(28, 30, 68, 30), line(28, 30, 48, 70), line(68, 30, 48, 70)],
    "Fork":              () => [line(20, 48, 48, 48), line(48, 48, 76, 26), line(48, 48, 76, 70), chevron(76, 26, 1, -1), chevron(76, 70, 1, 1)],
    "Inheritance":       () => [dot(48, 22, 3.2), line(48, 26, 28, 50), line(48, 26, 68, 50), line(28, 54, 18, 78), line(28, 54, 38, 78), line(68, 54, 58, 78), line(68, 54, 78, 78), dot(28, 54), dot(68, 54), dot(18, 78, 2), dot(38, 78, 2), dot(58, 78, 2), dot(78, 78, 2)],
    "No order":          () => [dot(24, 28), dot(70, 34), dot(40, 60), dot(76, 64), dot(22, 70), dot(50, 26)],
  },
};

export function markFor(suit, name) {
  const builder = MARKS[suit]?.[name];
  if (builder) return { paths: builder() };
  return { paths: [dot(48, 48, 3)] };
}

export function markForPack() {
  return {
    layers: [
      {
        suit: "relation",
        paths: [
          dot(24, 30), dot(48, 30), dot(72, 30),
          dot(24, 54), dot(48, 54), dot(72, 54),
          dot(24, 78), dot(48, 78), dot(72, 78),
        ],
      },
      {
        suit: "observation",
        paths: [ring(48, 48, 28)],
      },
      {
        suit: "action",
        paths: [
          { d: "M 20 76 C 36 30, 60 70, 78 22", kind: "line" },
          dot(78, 22, 2.6),
        ],
      },
    ],
  };
}
